import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import { boardQueryKeys } from "./boardQueryKeys";

/**
 * The board record for `boardId` in the current org.
 *
 * The query is disabled while the org is unknown or `boardId` is `null` or
 * empty. `boardId` comes from a route path param, so it is `null` when the
 * segment is absent and can be `""`, and an empty id would request the
 * malformed URL `v1/boards/id/`.
 *
 * A board that does not exist and another person's personal board both fail
 * with a 404 (`isBoardMissingOrUnavailable`).
 */
export function useBoard(boardId: string | null) {
  const { boardService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  return useQuery({
    queryKey: boardQueryKeys.id(boardId ?? "", orgId ?? ""),
    enabled: Boolean(boardId) && orgId !== undefined,
    queryFn: async ({ signal }) => {
      // `refetch()` runs a disabled query too, so the `enabled` condition is
      // checked again before any request goes out.
      if (orgId === undefined || boardId === null || boardId === "") {
        throw new Error("CurrentOrganization or boardId is not set");
      }
      return boardService.getBoardById(boardId, orgId, { abortSignal: signal });
    },
  });
}
